import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Model } from '../../models';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import useApiCallable from '../../api/use-api-callable';
import { QuestionRenderer } from '../questionnaire';
import { produce } from 'immer';

type QuestionnaireResponse = {
  question: string;
  answer: string | string[] | number | Record<string, string>;
  questionSlug?: string;
  answerSlug?: string;
};

interface EditSurgeryModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  healthResponses: QuestionnaireResponse[];
}

export default function EditSurgeryModal({
  open,
  onClose,
  userId,
  healthResponses,
}: EditSurgeryModalProps) {
  const { invalidateExact } = useInvalidation();
  const callApi = useApiCallable();

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});

  const surgeryQuestion = useMemo(
    () => Model.Questionnaire.getSurgeryQuestionObject(),
    [],
  );

  const getResponseByQuestion = useCallback(
    (questionText: string) => {
      const response = healthResponses.find((r) => r.question === questionText);
      return response?.answer || '';
    },
    [healthResponses],
  );

  useEffect(() => {
    if (!open || !surgeryQuestion) return;

    const response = getResponseByQuestion(surgeryQuestion.question || '');
    const initialData: Record<string, any> = {};
    const initialOtherTexts: Record<string, string> = {};

    if (surgeryQuestion.type === 'multi-select') {
      if (Array.isArray(response)) {
        initialData[surgeryQuestion.id] = response;
      } else if (typeof response === 'string') {
        initialData[surgeryQuestion.id] =
          response && response !== '-'
            ? response.split(', ').filter(Boolean)
            : [];
      } else {
        initialData[surgeryQuestion.id] = [];
      }
    } else if (surgeryQuestion.type === 'number') {
      initialData[surgeryQuestion.id] = response
        ? parseInt(String(response), 10)
        : 0;
    } else {
      if (
        response &&
        response !== '-' &&
        response !== 'Yes' &&
        response !== 'No'
      ) {
        initialData[surgeryQuestion.id] = 'Yes';
        initialOtherTexts[surgeryQuestion.id] = String(response);
      } else {
        initialData[surgeryQuestion.id] = response || '';
      }
    }

    setFormData(initialData);
    setOtherTexts(initialOtherTexts);
  }, [open, surgeryQuestion, getResponseByQuestion]);

  const formatAnswer = useCallback(
    (value: any, questionId: string) => {
      if (value === 'Other' && otherTexts[questionId]) {
        return otherTexts[questionId];
      }
      if (value === 'Yes' && otherTexts[questionId]) {
        return otherTexts[questionId];
      }
      return value.toString();
    },
    [otherTexts],
  );

  const handleMultiSelectChange = useCallback(
    (questionId: string, value: string, checked: boolean) => {
      setFormData((prev) => {
        const currentValues = prev[questionId] || [];
        if (checked) {
          return { ...prev, [questionId]: [...currentValues, value] };
        } else {
          return {
            ...prev,
            [questionId]: currentValues.filter((v: string) => v !== value),
          };
        }
      });
    },
    [],
  );

  const handleSingleSelectChange = useCallback(
    (questionId: string, value: string) => {
      setFormData((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const handleNumberChange = useCallback(
    (questionId: string, value: number) => {
      setFormData((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const handleOtherTextChange = useCallback(
    (questionId: string, value: string) => {
      setOtherTexts((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const shouldShowQuestion = useCallback(() => true, []);

  const handleSave = async () => {
    if (!surgeryQuestion) return;

    const completeHealthResponses = produce(healthResponses, (draft) => {
      const value = formData[surgeryQuestion.id];
      const existingIndex = draft.findIndex(
        (r) => r.question === surgeryQuestion.question,
      );

      if (value !== undefined && value !== null && value !== '') {
        const answerString = formatAnswer(value, surgeryQuestion.id);
        if (answerString) {
          if (existingIndex >= 0) {
            draft[existingIndex].answer = answerString;
          } else {
            draft.push({
              question: surgeryQuestion.question || '',
              answer: answerString,
            });
          }
        }
      } else if (existingIndex >= 0) {
        draft.splice(existingIndex, 1);
      }
    });

    await callApi(`/users/${userId}/questionnaires/before_appointment`, {
      method: 'PATCH',
      jsonBody: {
        responses: completeHealthResponses,
      },
    });

    toast.success('Surgery information updated');
    invalidateExact(`/users/${userId}/questionnaires/before_appointment`);
    onClose();
  };

  if (!surgeryQuestion) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack>
          <Typography variant="h6">Surgery to Reproductive Organs</Typography>
          <Typography variant="body2" color="text.secondary">
            Users have the ability to overwrite your changes in the app
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <QuestionRenderer
            question={surgeryQuestion}
            value={formData[surgeryQuestion.id]}
            otherTexts={otherTexts}
            onMultiSelectChange={handleMultiSelectChange}
            onSingleSelectChange={handleSingleSelectChange}
            onNumberChange={handleNumberChange}
            onOtherTextChange={handleOtherTextChange}
            shouldShowQuestion={shouldShowQuestion}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
